import { Box, CircularProgress, DialogActions, IconButton, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import FeatherIcon from "feather-icons-react";
import { Formik } from "formik";
import React from "react";
import { useSnackbar } from "../../components/toast/index.jsx";
import { DraggableDialog } from "../../consts.js";
import { COMPANY_CREATED_FAILED, COMPANY_CREATED_SUCCESS } from "../../helpers/messages.js";
import { createGroupAPI } from "../../services/group.js";
import Utils from "../../utils.js";
import S3_Util from "../../utils/s3-folder-creation/s3-folder-utilty.js";
import { CreateCompanySchema } from "./validator.js";

function CreateCompanyComponent({ open, onClose }) {
    const showSnackbar = useSnackbar();
    const initialValues = {
        name: "",
        contact: "",
        email: ""
    }

    /**
     * Submit Method to create a company
     */
    const submitForm = async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
            // Generate a sanitized folder name for S3
            const groupName = values.name.replace(/ /g, "").replace(/[^a-zA-Z0-9]/g, "");

            // Create an S3 folder via Lambda
            await S3_Util.callLambda(groupName);
            await createGroupAPI({
                GroupName: groupName,
            });
            let admintracker = null;
            try {
                admintracker = await Utils.genericGet("getCompany", "admin");
            } catch (error) {
                console.error("###@@@@@ Error jan 28 at 14:59 @@@@###", error);
            }

            let createdCompany = await Utils.genericMutation("createCompany", {
                name: values.name,
                contact: values.contact,
                email: values.email,
                status: "active",
                internalname: groupName,
                groups: [groupName, "admin"],
                trackers: admintracker?.data?.getCompany?.trackers,
            });
            showSnackbar(COMPANY_CREATED_SUCCESS);
            setSubmitting(false);
            onClose({ createdCompany });
        } catch (error) {
            console.log("error create company = ", error)
            showSnackbar(COMPANY_CREATED_FAILED, 'error');
            setSubmitting(false)
        }
    };

    const handleClose = (event, reason) => {
        // Prevent closing the dialog on backdrop click or escape key press
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        event.stopPropagation();
        onClose(null);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="draggable-dialog-title"
            scroll="paper"
            disableEscapeKeyDown={true}
            fullWidth={true}
            maxWidth="xs"
            PaperComponent={DraggableDialog}
            onClick={(e) => e.stopPropagation()}
        >
            <DialogTitle
                id="draggable-dialog-title"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                gap={1}
            >
                Create Company
                <IconButton
                    className="draggable-cancel"
                    size="small"
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        color: theme.palette.grey[400],
                        marginRight: "-.5rem",
                    })}
                >
                    <FeatherIcon icon="x" />
                </IconButton>
            </DialogTitle>

            <Formik
                enableReinitialize
                validationSchema={CreateCompanySchema}
                initialValues={initialValues}
                onSubmit={(values, actions) => {
                    submitForm(values, actions);
                }}
            >
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    values,
                    errors,
                    touched,
                    isSubmitting,
                }) => (
                    <Box
                        component="form"
                        display="flex"
                        flexDirection="column"
                        flexGrow={1}
                        overflow="auto"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <DialogContent dividers>
                            <TextField
                                id="outlined-name"
                                variant="outlined"
                                sx={{ marginTop: 1 }}
                                fullWidth={true}
                                label="Company Name"
                                required
                                name={"name"}
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.name && !!errors.name}
                                helperText={touched.name ? errors.name : ''}
                                disabled={isSubmitting}
                            />
                            <TextField
                                id="outlined-contact"
                                variant="outlined"
                                sx={{ marginTop: 3 }}
                                fullWidth={true}
                                label="Contact Name"
                                required
                                name={"contact"}
                                value={values.contact}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.contact && !!errors.contact}
                                helperText={touched.contact ? errors.contact : ''}
                                disabled={isSubmitting}
                            />
                            <Stack direction="row" alignItems="center" gap={.5} marginTop={1} sx={{ width: "100%" }}>
                                <Typography variant="body2" color={'grey.600'} component="span" display="inline-flex"><FeatherIcon icon="info" size={16} /></Typography>
                                <Typography variant="body2" color={'grey.600'}>This person will be assigned as the admin.</Typography>
                            </Stack>
                            <TextField
                                id="outlined-email"
                                variant="outlined"
                                sx={{ marginTop: 3, marginBottom: 1 }}
                                fullWidth={true}
                                label="Email"
                                required
                                name={"email"}
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && !!errors.email}
                                helperText={touched.email ? errors.email : ''}
                                disabled={isSubmitting}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <CircularProgress color="inherit" size={24} /> : "Create"}
                            </Button>
                        </DialogActions>
                    </Box>
                )}
            </Formik>
        </Dialog>
    );
}

export default CreateCompanyComponent;